import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Observation, Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Anchor, Table } from "@mantine/core";

import * as Sentry from "@sentry/react";

import { TextWrapper } from "@/components/TextWrapper";
import DateRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/DateRow";
import AdmissionDischargeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/AdmissionDischargeRow";
import AssessmentNoteRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/AssessmentNoteRow";
import BirthDateTimeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthDateTimeRow";
import BirthingRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthingRow";
import CervicalExamRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/CervicalExamRow";
import ContractionsRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ContractionsRow";
import CumulativeBloodLossRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/CumulativeBloodLossRow";
import EstimatedBloodLossRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/EstimatedBloodLossRow";
import FHRRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/FHRRow";
import InputOutputRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/InputOutputRow";
import LaborVitalsRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/LaborVitalsRow";
import LocationRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/LocationRow";
import MedsAndTherapiesRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/MedsAndTherapiesRow";
import MembranesRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/MembranesRow";
import PositionRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/PositionRow";
import ResuscitationRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ResuscitationRow";
import ROMRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ROMRow";
import ShoulderDystociaRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ShoulderDystociaRow";
import TransportRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/TransportRow";
import {
  createLaborStageObservation,
  fetchLaborStageObservations,
  getLaborStageRows,
} from "@/encounters/components/EncounterLabor/services/labor";
import { LaborStage, StageRow } from "@/encounters/components/EncounterLabor/types/labor";
import {
  filterRowsWithObservations,
  isLaborObservationBeforeBirth,
} from "@/encounters/components/EncounterLabor/utils/labor";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { LaborFlowsheetForms, QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { selectBirthDateTime } from "@/store/slices/birth";
import { loadLaborStages } from "@/store/slices/labor-stages";
import { getCodeableConceptCode } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

interface LaborStageRowProps {
  clientId: string;
  encounterId: string;
  label: string;
  stage: LaborStage;
  observation?: Observation;
  showNumbersOnly?: boolean;
  isViewOnly?: boolean;
  flowsheetId?: string;
}

const ROW_COMPONENT_BY_FORM = {
  [LOINCCodes.FetalHeartRate]: FHRRow,
  [LOINCCodes.LaborVitalSigns]: LaborVitalsRow,
  [LaborFlowsheetForms.Contractions]: ContractionsRow,
  [LaborFlowsheetForms.CervicalExam]: CervicalExamRow,
  [LaborFlowsheetForms.ROM]: ROMRow,
  [LaborFlowsheetForms.Position]: PositionRow,
  [LaborFlowsheetForms.AssessmentNote]: AssessmentNoteRow,
  [LaborFlowsheetForms.Birthing]: BirthingRow,
  [LaborFlowsheetForms.InputOutput]: InputOutputRow,
  [LaborFlowsheetForms.Membranes]: MembranesRow,
  [LaborFlowsheetForms.MedsTherapies]: MedsAndTherapiesRow,
  [LaborFlowsheetForms.Location]: LocationRow,
  [LaborFlowsheetForms.ShoulderDystocia]: ShoulderDystociaRow,
  [LaborFlowsheetForms.AdmissionDischarge]: AdmissionDischargeRow,
  [LaborFlowsheetForms.EstimatedBloodLoss]: EstimatedBloodLossRow,
  [LOINCCodes.CumulativeBloodLoss]: CumulativeBloodLossRow,
  [LaborFlowsheetForms.Transport]: TransportRow,
  [LaborFlowsheetForms.Resuscitation]: ResuscitationRow,
  [QuiltedHealthCodes.BirthDateTime]: BirthDateTimeRow,
};

export default function LaborStageRows({
  clientId,
  encounterId,
  label,
  stage,
  observation,
  showNumbersOnly = false,
  isViewOnly = false,
  flowsheetId,
}: LaborStageRowProps) {
  const medplum = useMedplum();
  const profile = medplum.getProfile() as Practitioner;
  const { applyObservationAmendPreview } = useLaborAmendFlowsheetPersist();

  const dispatch = useDispatch();
  const birthDateTime = useSelector(selectBirthDateTime) || "";

  const [isLoading, setIsLoading] = useState(false);
  const [baseRows, setBaseRows] = useState<Record<string, StageRow[]>>({});

  useEffect(() => {
    let isCancelled = false;

    const loadRows = async () => {
      const fetchedRows = await getLaborStageRows(medplum, observation);
      if (!isCancelled) {
        setBaseRows(fetchedRows);
      }
    };

    void loadRows();

    return () => {
      isCancelled = true;
    };
  }, [medplum, observation, clientId, encounterId, stage]);

  const rows = useMemo(() => {
    const rowsWithPreview = Object.fromEntries(
      Object.entries(baseRows).map(([date, groups]) => [
        date,
        groups.reduce<StageRow[]>((groupsWithPreview, group) => {
          const observations = group.observations.map((memberObservation) =>
            applyObservationAmendPreview(memberObservation),
          );
          if (observations.length > 0) {
            groupsWithPreview.push({ ...group, observations });
          }
          return groupsWithPreview;
        }, []),
      ]),
    );

    const rowsMatchingStageMembers = Object.fromEntries(
      filterRowsWithObservations<StageRow>(rowsWithPreview),
    );

    if (!birthDateTime) {
      return rowsMatchingStageMembers;
    }

    const mappedRows = Object.fromEntries(
      Object.entries(rowsMatchingStageMembers).map(([date, groups]) => [
        date,
        groups.map((group) => ({
          ...group,
          observations: group.observations.filter((memberObservation: Observation) =>
            isLaborObservationBeforeBirth(memberObservation, birthDateTime),
          ),
        })),
      ]),
    );

    return Object.fromEntries(filterRowsWithObservations<StageRow>(mappedRows));
  }, [applyObservationAmendPreview, baseRows, birthDateTime]);

  const handleAddLaborStage = async () => {
    try {
      setIsLoading(true);
      await createLaborStageObservation(medplum, clientId, encounterId, profile, stage);
      // Refetch all labor stages and update Redux
      const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);
      dispatch(loadLaborStages(observations));
    } catch (error) {
      showSystemErrorNotification();
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Table.Tr bg="white">
        {observation && observation.status !== "cancelled" ? (
          <Table.Td
            bg="gray.2"
            ta="center"
            colSpan={12}
            style={{ borderBottom: "4px solid var(--mantine-color-gray-4)" }}
          >
            <TextWrapper c="gray.7" variant="bold">
              {label.toUpperCase()}
            </TextWrapper>
          </Table.Td>
        ) : (
          <Table.Td ta="center" colSpan={12}>
            {!isViewOnly ? (
              <Anchor component="button" disabled={isLoading} onClick={handleAddLaborStage}>
                <TextWrapper fz="md" c="burgundy.9" variant="bold" td="underline">
                  + {label}
                </TextWrapper>
              </Anchor>
            ) : (
              <TextWrapper c="gray.7" variant="bold">
                {label.toUpperCase()}
              </TextWrapper>
            )}
          </Table.Td>
        )}
      </Table.Tr>
      {(() => {
        return Object.entries(rows).map(([date, groups]) => [
          <DateRow date={date} key={`date-${date}`} />,
          ...groups.flatMap(({ time, observations }) => {
            const observationElements = observations.map((observation) => {
              const code = getCodeableConceptCode(observation.code);

              const RowComponent =
                ROW_COMPONENT_BY_FORM[code as keyof typeof ROW_COMPONENT_BY_FORM];

              if (!RowComponent) return null;

              return (
                <RowComponent
                  time={time}
                  key={observation.id}
                  isViewOnly={isViewOnly}
                  observation={observation}
                  flowsheetId={flowsheetId}
                  showNumbersOnly={showNumbersOnly}
                />
              ) as React.ReactElement;
            });

            return observationElements;
          }),
        ]);
      })()}
    </>
  );
}
